import { assertUnreachable } from "@lib/utils/assertUnreachable";
import type { RemovedAction, StampableAction } from "./actions";

// Converts historical/removed actions into the current reducer schema.
//
// Design rule:
// - If a removed action still carries meaningful state, translate it to an
//   equivalent current action.
// - If it is obsolete and has no effect in the current model, translate to NOOP.
export default function upcastAction(
  removedAction: RemovedAction,
): StampableAction {
  switch (removedAction.type) {
    case "EVALUATION_STATUS_CHANGE": {
      const { evaluatorId, evaluableId, change, deferredTo, legacy } =
        removedAction;
      if (change === "hide" || change === "unhide") {
        return {
          type: "EVALUATION_SET_HIDDEN",
          evaluatorId,
          evaluableId,
          hidden: change === "hide",
        };
      } else {
        return {
          type: "EVALUATION_STATE_CHANGE",
          evaluatorId,
          evaluableId,
          change,
          deferredTo,
          legacy,
        };
      }
    }
    case "ODSR_MASTER_ADJUSTMENT_CHANGE":
    case "SET_EXPECTED_MATCHING_FRACTION":
    case "RECOMMENDER_OUTSIDE_DOLLAR_SUBSTITUTION_RATE_CHANGE":
      return { type: "NOOP" };
    default:
      assertUnreachable(removedAction);
  }
}

type CreateRoundAction = Extract<StampableAction, { type: "CREATE_ROUND" }>;
type CreateRoundCurrent = Omit<CreateRoundAction, "version"> & {
  version: 2;
};

export function upcastCurrentAction(action: StampableAction): StampableAction {
  if (action.type !== "CREATE_ROUND") {
    return action;
  }

  if (action.version === 2) {
    return action;
  }

  const isSfcRound = action.isSfcRound !== false;
  const trimmedRoundIdentifier = action.roundIdentifier?.trim();

  const createRoundV2Payload: CreateRoundCurrent = {
    ...action,
    version: 2,
    roundIdentifier: isSfcRound
      ? (trimmedRoundIdentifier ?? "2025")
      : undefined,
  };

  return createRoundV2Payload as StampableAction;
}
